import api from "./api";

// ── Types ──

interface UserItem {
  id: number;
  username: string;
  email: string;
  full_name: string | null;
  employee_id: string | null;
  approved_at?: string | null;
  department: string | null;
  job_title: string | null;
  department_id: number | null;
  job_type_id: number | null;
  work_stage_id?: number | null;
  department_name: string | null;
  job_type_name: string | null;
  work_stage_name?: string | null;
  is_active: boolean;
  is_superuser: boolean;
  role_id: number | null;
  role_name: string | null;
  created_at: string;
}

interface RoleItem {
  id: number;
  name: string;
  description: string | null;
  is_system: boolean;
  user_count: number;
}

interface PermissionItem {
  id: number;
  name: string;
  code: string;
  resource: string;
  action: string;
  description: string | null;
}

interface DepartmentItem {
  id: number;
  name: string;
  description: string | null;
  user_count: number;
}

interface JobTypeItem {
  id: number;
  name: string;
  description: string | null;
  user_count: number;
}

interface MenuItem {
  id: number;
  name: string;
  path: string;
  icon: string;
  group_id: number | null;
  group_name: string | null;
  group_icon?: string | null;
  group_sort?: number | null;
  sort: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface MenuGroupItem {
  id: number;
  name: string;
  icon: string;
  sort: number;
  created_at: string;
  menu_count: number;
}

interface DataPermissionRule {
  id: number;
  name: string;
  scope_type: string;
  related_objects: string;
  account_count: number;
  created_by: number | null;
  created_at: string | null;
}

interface PasswordResetRequestItem {
  id: number;
  username: string;
  note: string | null;
  status: string;
  created_at: string;
  processed_at: string | null;
}

// ── Users ──

const users = {
  list: (skip = 0, limit = 50) =>
    api.get<UserItem[]>(`/admin/users?skip=${skip}&limit=${limit}`),
  get: (id: number) => api.get<UserItem>(`/admin/users/${id}`),
  create: (data: {
    username: string;
    password: string;
    email?: string;
    full_name?: string;
    role_id?: number;
    department_id?: number;
    job_type_id?: number;
    work_stage_id?: number;
  }) => api.post<{ id: number; username: string }>("/auth/register", data),
  update: (id: number, data: Partial<UserItem>) =>
    api.put<{ message: string }>(`/admin/users/${id}`, data),
  delete: (id: number) =>
    api.delete<{ message: string }>(`/admin/users/${id}`),
  reject: (id: number) =>
    api.post<{ message: string }>(`/admin/users/${id}/reject`),
  listPasswordResetRequests: () =>
    api.get<PasswordResetRequestItem[]>("/admin/password-reset-requests"),
  processPasswordResetRequest: (id: number, status: string) =>
    api.put<{ message: string }>(`/admin/password-reset-requests/${id}`, { status }),
  resetPassword: (id: number, newPassword: string) =>
    api.put<{ message: string }>(`/admin/users/${id}/reset-password`, { new_password: newPassword }),
};

// ── Roles ──

const roles = {
  list: () => api.get<RoleItem[]>("/admin/roles"),
  create: (data: { name: string; description?: string }) =>
    api.post<{ id: number; name: string }>("/admin/roles", data),
  update: (id: number, data: { name?: string; description?: string }) =>
    api.put<{ message: string }>(`/admin/roles/${id}`, data),
  delete: (id: number) =>
    api.delete<{ message: string }>(`/admin/roles/${id}`),
};

// ── Permissions ──

const permissions = {
  list: () => api.get<PermissionItem[]>("/admin/permissions"),
  getForRole: (roleId: number) =>
    api.get<number[]>(`/admin/roles/${roleId}/permissions`),
  create: (data: {
    name: string;
    code: string;
    resource: string;
    action: string;
    description?: string;
  }) => api.post<{ id: number; code: string }>("/admin/permissions", data),
  delete: (id: number) =>
    api.delete<{ message: string }>(`/admin/permissions/${id}`),
  addToRole: (roleId: number, permId: number) =>
    api.post<{ message: string }>(
      `/admin/roles/${roleId}/permissions/${permId}`
    ),
  removeFromRole: (roleId: number, permId: number) =>
    api.delete<{ message: string }>(
      `/admin/roles/${roleId}/permissions/${permId}`
    ),
};

// ── Departments ──

const departments = {
  list: () => api.get<DepartmentItem[]>("/admin/departments"),
  create: (data: { name: string; description?: string }) =>
    api.post<{ id: number; name: string }>("/admin/departments", data),
  update: (id: number, data: { name?: string; description?: string }) =>
    api.put<{ message: string }>(`/admin/departments/${id}`, data),
  delete: (id: number) =>
    api.delete<{ message: string }>(`/admin/departments/${id}`),
};

// ── Job Types ──

const jobTypes = {
  list: () => api.get<JobTypeItem[]>("/admin/job-types"),
  create: (data: { name: string; description?: string }) =>
    api.post<{ id: number; name: string }>("/admin/job-types", data),
  update: (id: number, data: { name?: string; description?: string }) =>
    api.put<{ message: string }>(`/admin/job-types/${id}`, data),
  delete: (id: number) =>
    api.delete<{ message: string }>(`/admin/job-types/${id}`),
};

// ── Menus ──

const menus = {
  list: () => api.get<MenuItem[]>("/menu/items"),
  create: (data: {
    name: string;
    path: string;
    icon?: string;
    group_id?: number;
    sort?: number;
    enabled?: boolean;
  }) => api.post<{ id: number; name: string }>("/menu/items", data),
  update: (id: number, data: Partial<MenuItem>) =>
    api.put<{ message: string }>(`/menu/items/${id}`, data),
  delete: (id: number) =>
    api.delete<{ message: string }>(`/menu/items/${id}`),
  myItems: () => api.get<MenuItem[]>("/menu/my-items"),
};

// ── Menu Groups ──

const menuGroups = {
  list: () => api.get<MenuGroupItem[]>("/menu/groups"),
  create: (data: { name: string; icon?: string; sort?: number }) =>
    api.post<{ id: number; name: string }>("/menu/groups", data),
  update: (id: number, data: { name?: string; icon?: string; sort?: number }) =>
    api.put<{ message: string }>(`/menu/groups/${id}`, data),
  delete: (id: number) =>
    api.delete<{ message: string }>(`/menu/groups/${id}`),
};

// ── Role-Menu Binding ──

const roleMenus = {
  get: (roleId: number) => api.get<number[]>(`/menu/roles/${roleId}/menus`),
  set: (roleId: number, menuIds: number[]) =>
    api.put<{ message: string }>(`/menu/roles/${roleId}/menus`, {
      menu_ids: menuIds,
    }),
};

// ── Data Permissions ──

const dataPermissions = {
  listRules: () =>
    api.get<DataPermissionRule[]>("/data-permissions/rules"),
  createRule: (data: {
    name: string;
    scope_type: string;
    related_objects?: string;
  }) =>
    api.post<{ id: number; name: string }>("/data-permissions/rules", data),
  updateRule: (
    id: number,
    data: { name?: string; scope_type?: string; related_objects?: string }
  ) => api.put<{ message: string }>(`/data-permissions/rules/${id}`, data),
  deleteRule: (id: number) =>
    api.delete<{ message: string }>(`/data-permissions/rules/${id}`),
  getDepartments: () => api.get<string[]>("/data-permissions/departments"),
  getJobTypes: () => api.get<string[]>("/data-permissions/job-types"),
  getUsers: () => api.get<string[]>("/data-permissions/users"),
  getRoles: () => api.get<string[]>("/data-permissions/roles"),
};

// ── Settings ──

// ── Work Stages ──

const workStages = {
  list: (search?: string) => {
    const params = search ? `?search=${encodeURIComponent(search)}` : "";
    return api.get<{ id: number; name: string; work_type?: string }[]>(`/work-stages${params}`);
  },
  create: (data: { name: string; work_type?: string }) =>
    api.post<{ id: number; name: string }>("/work-stages", data),
  update: (id: number, data: { name?: string; work_type?: string }) =>
    api.put<{ message: string }>(`/work-stages/${id}`, data),
  delete: (id: number) =>
    api.delete<{ message: string }>(`/work-stages/${id}`),
};

const settings = {
  get: () =>
    api.get<Record<string, string>>("/settings"),
  update: (data: Record<string, string>) =>
    api.put<{ message: string }>("/settings", data),
};

export type {
  UserItem,
  RoleItem,
  PermissionItem,
  DepartmentItem,
  JobTypeItem,
  MenuItem,
  MenuGroupItem,
  DataPermissionRule,
  PasswordResetRequestItem,
};

export {
  users,
  roles,
  permissions,
  departments,
  jobTypes,
  menus,
  menuGroups,
  roleMenus,
  dataPermissions,
  workStages,
  settings,
};
